import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  History,
  Add,
  Person,
  Refresh,
  Visibility,
  CheckCircle,
  Warning,
  Schedule,
  Menu,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import scanHistoryService from '../services/scanHistoryService';
import authService from '../services/auth';

const ProfessionalSidebar = ({ onSelectScan, onViewAllScans, isOpen, onToggle }) => {
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadUser();
    loadRecentScans();
  }, []);

  useEffect(() => {
    const onHistory = () => loadRecentScans();
    window.addEventListener('scanHistoryUpdated', onHistory);
    return () => window.removeEventListener('scanHistoryUpdated', onHistory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    try {
      setUserLoading(true);
      const response = await authService.getProfile();
      // Extract user data from the response structure
      const userData = response?.data?.user || response?.user || response;
      setUser(userData);
    } catch (error) {
      setUser(null);
    } finally {
      setUserLoading(false);
    }
  };

  const loadRecentScans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await scanHistoryService.getRecentScans(5);
      setRecentScans(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to load recent scans');
      setRecentScans([]);
    } finally {
      setLoading(false);
    }
  };


  const handleSelectScan = (scan) => {
    onSelectScan(scan);
  };

  const handleViewAllScans = () => {
    if (onViewAllScans) {
      onViewAllScans();
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#ff9800';
    if (score >= 40) return '#f44336';
    return '#9e9e9e';
  };

  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case 'STRONG FIT': return '#4caf50';
      case 'MODERATE FIT': return '#ff9800';
      case 'WEAK FIT': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const formatRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatScan = (scan) => {
    const formatted = scanHistoryService.formatScanForDisplay(scan);
    return formatted;
  };

  return (
    <Box
      sx={{
        width: '320px',
        height: '100vh',
        bgcolor: 'background.default',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s ease-in-out',
        overflow: 'hidden',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        boxShadow: isOpen ? '2px 0 8px rgba(0,0,0,0.1)' : 'none'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2.5, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, rgb(21, 119, 197) 0%, rgb(16, 95, 157) 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: 'white', fontSize: '1.1rem' }}>
            NovaMatch
          </Typography>
          <IconButton 
            size="small" 
            onClick={onToggle} 
            title={isOpen ? "Close sidebar" : "Open sidebar"}
            sx={{ 
              position: 'absolute',
              right: 0,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <Menu fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5, display: 'block', fontSize: '0.7rem', fontWeight: 500 }}>
          AI-Powered Recruitment Analysis
        </Typography>
      </Box>

      {/* Recent Scans Section */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: '1rem' }}>
              Recent Scans
            </Typography>
            <IconButton 
              size="small" 
              onClick={loadRecentScans} 
              disabled={loading}
              sx={{ 
                color: 'rgb(21, 119, 197)',
                bgcolor: 'rgba(21, 119, 197, 0.08)',
                '&:hover': { 
                  bgcolor: 'rgba(21, 119, 197, 0.15)',
                  transform: 'rotate(180deg)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" size="small" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : !Array.isArray(recentScans) || recentScans.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <History sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No recent scans
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Start analyzing resumes to see them here
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 1 }}>
              {(recentScans || []).map((scan, index) => {
                const formattedScan = formatScan(scan);
                return (
                  <Box
                    key={scan.scanId || index}
                    onClick={() => handleSelectScan(scan)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'rgba(21, 119, 197, 0.2)',
                      bgcolor: 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f9fbfd 100%)',
                      '&:hover': {
                        borderColor: 'rgb(21, 119, 197)',
                        boxShadow: '0 4px 12px rgba(21, 119, 197, 0.2)',
                        transform: 'translateY(-2px)',
                        background: 'linear-gradient(135deg, #ffffff 0%, #eef6fc 100%)',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.3,
                            fontSize: '0.85rem',
                            color: 'text.primary'
                          }}
                        >
                          {formattedScan.jobTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}
                        >
                          {formattedScan.company}
                        </Typography>
                      </Box>
                      <Chip
                        label={formattedScan.overallScore}
                        size="small"
                        sx={{
                          height: 26,
                          fontSize: '0.75rem',
                          bgcolor: getScoreColor(formattedScan.overallScore),
                          color: 'white',
                          fontWeight: 800,
                          minWidth: 38,
                          boxShadow: `0 2px 8px ${getScoreColor(formattedScan.overallScore)}40`
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Schedule sx={{ fontSize: 12, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {formatRelativeTime(formattedScan.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* View All Button */}
        <Box sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<History />}
            onClick={handleViewAllScans}
            size="medium"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              py: 1,
              px: 2,
              fontSize: '0.85rem',
              background: 'linear-gradient(135deg, rgb(21, 119, 197) 0%, rgb(16, 95, 157) 100%)',
              boxShadow: '0 2px 8px rgba(21, 119, 197, 0.25)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(21, 119, 197, 0.35)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            View All Scans
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* User Section */}
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '1rem',
              fontWeight: 700
            }}
          >
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.5
              }}
            >
              {userLoading ? 'My Profile' : 
               (user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.email || 'My Profile')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userLoading ? 'Account' :
               (user?.accountId ? `Account: ${user.accountId.slice(0, 8)}...` : user?.email || 'Account')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfessionalSidebar;
